import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Lock, Mail, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [hideDemo, setHideDemo] = useState(false);
  const [demoCount, setDemoCount] = useState(0);
  const [savingDemo, setSavingDemo] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setHideDemo(!!r.data.hide_demo_items);
      setDemoCount(r.data.demo_count || 0);
    }).catch(() => {});
  }, []);

  const toggleHideDemo = async (value) => {
    setSavingDemo(true);
    setHideDemo(value);
    try {
      await api.put("/admin/settings", { hide_demo_items: value });
      toast.success(value ? "Demo items hidden from storefront" : "Demo items visible again");
    } catch (err) {
      setHideDemo(!value);
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSavingDemo(false); }
  };

  const deleteAllDemos = async () => {
    if (!window.confirm(`Delete all ${demoCount} demo items? This can't be undone.`)) return;
    try {
      const { data } = await api.delete("/admin/demo-items");
      toast.success(`Deleted ${data.deleted} demo items`);
      setDemoCount(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Password must be 8+ chars"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed · use it next time you log in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const sendDigest = async (period) => {
    setDigestLoading(true);
    try {
      const { data } = await api.post(`/admin/digest/send?period=${period}`);
      toast.success(`${period === "weekly" ? "Weekly" : "Daily"} digest sent to ${data.sent_to} admin${data.sent_to === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setDigestLoading(false); }
  };

  return (
    <div className="mt-6 max-w-md flex flex-col gap-6" data-testid="settings-tab">
      <div className="brut p-6">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={20} strokeWidth={3} />
          <h3 className="font-display text-2xl uppercase">Change password</h3>
        </div>
        <p className="text-sm opacity-70 mb-4">Update your admin login. Use 8+ characters.</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="block">
            <span className="text-sm font-bold uppercase tracking-wider">Current password</span>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="brut-input w-full mt-1" data-testid="settings-current" autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="text-sm font-bold uppercase tracking-wider">New password</span>
            <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} className="brut-input w-full mt-1" data-testid="settings-new" autoComplete="new-password" />
          </label>
          <label className="block">
            <span className="text-sm font-bold uppercase tracking-wider">Confirm new password</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="brut-input w-full mt-1" data-testid="settings-confirm" autoComplete="new-password" />
          </label>
          <button disabled={loading} type="submit" className="brut-btn bg-orange text-white py-3 mt-2" data-testid="settings-submit">
            {loading ? "Saving…" : "Update password"}
          </button>
        </form>
      </div>

      <div className="brut p-6">
        <div className="flex items-center gap-2 mb-1">
          <EyeOff size={20} strokeWidth={3} />
          <h3 className="font-display text-2xl uppercase">Demo items</h3>
        </div>
        <p className="text-sm opacity-70 mb-4">
          {demoCount > 0
            ? `${demoCount} demo items are tagged. Hide them from your storefront while you build out real inventory, or wipe them when you're ready to go live.`
            : "No demo items tagged — you're running clean."}
        </p>
        <label className="flex items-center gap-3 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={hideDemo}
            disabled={savingDemo || demoCount === 0}
            onChange={(e) => toggleHideDemo(e.target.checked)}
            className="w-5 h-5"
            data-testid="hide-demo-toggle"
          />
          <span className="font-bold uppercase tracking-wider text-sm">
            Hide demo items from shoppers
          </span>
        </label>
        <button
          onClick={deleteAllDemos}
          disabled={demoCount === 0}
          className="brut-btn bg-pink text-white px-4 py-2 flex items-center gap-2 disabled:opacity-50"
          data-testid="delete-demos-btn"
        >
          <Trash2 size={16} strokeWidth={3} /> Delete all demo items ({demoCount})
        </button>
      </div>

      <div className="brut p-6">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={20} strokeWidth={3} />
          <h3 className="font-display text-2xl uppercase">Digest emails</h3>
        </div>
        <p className="text-sm opacity-70 mb-4">
          Daily digest auto-sends at 08:00 UTC; weekly digest goes every Monday 08:00 UTC. Send one now to test.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button disabled={digestLoading} onClick={() => sendDigest("daily")} className="brut-btn bg-yellow px-4 py-3 flex-1" data-testid="send-daily-digest">
            {digestLoading ? "…" : "Send daily now"}
          </button>
          <button disabled={digestLoading} onClick={() => sendDigest("weekly")} className="brut-btn bg-cyan px-4 py-3 flex-1" data-testid="send-weekly-digest">
            {digestLoading ? "…" : "Send weekly now"}
          </button>
        </div>
      </div>
    </div>
  );
}
